/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import Alert from 'react-s-alert';
import Joyride, { STATUS } from 'react-joyride';
import Select from 'react-select';
import moment from 'moment';

// context imports
import { ThemeContext } from '../../../contexts/ThemeContext';
import { AuthContext } from '../../../contexts/AuthContext';

// service imports
import AuthService from '../../../services/AuthService';
import GeneralService from '../../../services/GeneralService';
import DemoService from '../../../services/DemoService';

// local imports
import ErrorBoundary from '../../general/ErrorBoundary';
import ProviderNavbar from './ProviderNavbar';
import Message from '../../general/Message';
import { counsellors } from '../demoData';

const Settings = props => {

    // useStates
    const [user, setUser] = useState({ firstName: "", lastName: "", email: "", selectedRole: '', roles: [], profileImage: "", demoTakenFor: [] });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
        const [updatePasswordModal, setUpdatePasswordModal] = useState({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ msgBody: '', msgError: false });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>Manage settings related to your account from here!</h2>,
                placement: 'center',
                target: '#settingsDiv',
            },
            {
                content: <div className="tooltipDiv">Update your account password by pressing this button.</div>,
                placement: 'right',
                target: '#updatePwdBtn',
            }
        ]
    });
    let helpers = null;

    // contexts
    const { timeZones, selectedTimeZone, setSelectedTimeZone } = useContext(AuthContext);
    const { isGoogleSignIn } = useContext(AuthContext);
    const { theme, fontFamily, fontSize } = useContext(ThemeContext);

    // useEffects
    useEffect(() => {
        DemoService.getUserData().then(data => {
            if (data.isAuthenticated) {
                if (data.user.roles.includes("DemoProvider")) {
                    setUser(data.user);
                    if (!data.user.demoTakenFor.includes("ProviderSettings")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                } else {
                    // props.history.replace('PagePath');
                }
            } else {
                props.history.replace('/demo/signin');
            }
        })
    }, [])


    // handle functions
    const handlePasswordSubmit = () => {
        try {
            if (updatePasswordModal.data.newPassword.length < 6) {
                setMessage({
                    msgBody: "Your password must be at least 6 characters",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[a-zA-Z]/i) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one letter",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[0-9]/) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one digit",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[A-Z]/) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one uppercase letter",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword !== updatePasswordModal.data.reNewPassword) {
                setMessage({
                    msgBody: "Passwords don't match",
                    msgError: true
                })
            } else {
                setIsLoading(true);
                DemoService.updatePassword(updatePasswordModal.data.oldPassword, updatePasswordModal.data.newPassword)
                    .then(data => {
                        setUpdatePasswordModal({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } });
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        setIsLoading(false);
                    })
                    .catch(error => {
                        setMessage(error.message);
                        setIsLoading(false);
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Provider ProviderSettings handlePasswordSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    
        if (finishedStatuses.includes(status)) {
            if (!user.demoTakenFor.includes("ProviderSettings")) {
                DemoService.markVisited("ProviderSettings");
                setUser({ ...user, demoTakenFor: [...user.demoTakenFor, "ProviderSettings"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    return (
        <div id="settingsDiv">
            <ErrorBoundary errorAt="At Provider Settings Joyride">
                <Joyride
                    callback={handleJoyrideCallback}
                    continuous={true}
                    getHelpers={getHelpers}
                    run={tutorial.status}
                    scrollToFirstStep={true}
                    showProgress={false}
                    showSkipButton={false}
                    steps={tutorial.steps}
                    locale={{ last: 'Finish' }}
                    styles={{
                        options: {
                            primaryColor: "rgb(56, 78, 115)",
                            zIndex: 10000,
                        },
                    }}
                />
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Provider Settings ProviderNavbar">
                <ProviderNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
            </ErrorBoundary>
            <div className="p-3">
                <div>
                    <h1 style={theme.headingBold}>Settings</h1>
                    <hr style={{ borderWidth: "2px" }} />
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '10rem', fontSize: '1.2rem', fontWeight: '600' }}>Change timezone</label>
                    <div style={{ width: '14rem', marginLeft: '1rem' }}>
                        <Select
                            value={selectedTimeZone}
                            onChange={(e) => setSelectedTimeZone(Object.assign({ isDST: moment().isDST() }, e))}
                            options={timeZones}
                        />
                    </div>
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '10rem', fontSize: '1.2rem', fontWeight: '600' }}>Change password</label>
                    <Button type="button" id="updatePwdBtn" className="btn ml-3" style={theme.button} onClick={() => setUpdatePasswordModal({ isVisible: true, data: { oldPassword: "", newPassword: "", reNewPassword: "" } })}>
                        Change
                    </Button>
                </div>

            </div>
            <ErrorBoundary errorAt="At Provider Settings Modal">
                <Modal size="sm" isOpen={updatePasswordModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setUpdatePasswordModal({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Change password</span></ModalHeader>
                    <ModalBody>
                        {message.msgBody ? <Message message={message} /> : null}
                        <Form>
                            <FormGroup>
                                <Label>Old password</Label>
                                <Input type="password" value={updatePasswordModal.data.oldPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, oldPassword: e.target.value } })} />
                            </FormGroup>
                            <FormGroup>
                                <Label>New password</Label>
                                <Input type="password" value={updatePasswordModal.data.newPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, newPassword: e.target.value } })} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Confirm password</Label>
                                <Input type="text" value={updatePasswordModal.data.reNewPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, reNewPassword: e.target.value } })} />
                            </FormGroup>
                        </Form>
                        <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>*Password must have at least 6 characters with one alphabet, one digit and one uppercase</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handlePasswordSubmit()} disabled={isLoading}>
                            {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Update
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </div>
    )

}

export default Settings;